import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import ExportButton from "../ExportButton";
import { exportPurchaseOrders } from "../../services";
import { PurchaseOrderListFilter } from "../../types/purchase-order.type";
import { formatNumber, numberWithCommas } from "../../utils";
import PurchaseOrderPagination from "./Pagination";
import PurchaseOrderSkeleton from "./Skeleton";
import PurchaseOrderStatusChip from "./StatusChip";
import { usePurchaseOrderList } from "./usePurchaseOrderList";
import {
  customerRefText,
  formatCalendarDate,
  PO_COMPACT_MEDIA,
  PO_EMPTY_TEXT,
} from "./data";

const DRAWER_WIDTH = 720;
const COLUMN_COUNT = 5;
const ROW_HEIGHT = 44;

/** ช่องของตารางสาย × วันที่ถูกคลิก — กลายเป็นตัวกรองของรายการ PO ชุดเดิม */
export interface WeeklySlice {
  /** หัวของ drawer เช่น "สาย 03 · พ. 24 ก.ย. 69" */
  title: string;
  filter: PurchaseOrderListFilter;
}

interface WeeklySliceDrawerProps {
  /** null = ปิดอยู่ */
  slice: WeeklySlice | null;
  onClose: () => void;
  onOpenDetail: (uuid: string) => void;
}

interface SliceListProps {
  slice: WeeklySlice;
  onOpenDetail: (uuid: string) => void;
}

/**
 * รายการ PO ของช่องที่คลิก — อ่านจาก GET /purchase-orders ตัวเดียวกับตาราง "รายการ"
 * ด้วยตัวกรองวันส่ง + สาย จำนวนในนี้จึงมาจาก API ชุดเดียวกับไฟล์ Export เสมอ
 * · แยกเป็น component ที่ mount เฉพาะตอนเปิด เพื่อไม่ให้ยิงคำขอขณะ drawer ปิดอยู่
 */
function SliceList({ slice, onOpenDetail }: SliceListProps) {
  const list = usePurchaseOrderList(slice.filter);

  const handleExport = () => exportPurchaseOrders(slice.filter);

  return (
    <Stack gap={1.5}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
      >
        <Typography variant="body2" color="text.secondary">
          {numberWithCommas(list.totalCount)} ใบ
        </Typography>
        <ExportButton
          request={handleExport}
          fallbackFilename={`purchase-orders_${dayjs().format("YYYYMMDD")}.xlsx`}
          disabled={list.totalCount === 0}
        />
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            <TableRow>
              <TableCell>ร้าน</TableCell>
              <TableCell>เลข PO</TableCell>
              <TableCell>วันส่ง</TableCell>
              <TableCell align="right">ยอด</TableCell>
              <TableCell align="center">สถานะ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.loading && (
              <PurchaseOrderSkeleton
                columns={COLUMN_COUNT}
                rows={Math.min(list.limit, 10)}
                rowHeight={ROW_HEIGHT}
              />
            )}

            {!list.loading && list.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูลตามเงื่อนไข
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!list.loading &&
              list.rows.map((row) => (
                <TableRow
                  key={row.uuid}
                  hover
                  onClick={() => onOpenDetail(row.uuid)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {customerRefText(row.customer) ||
                        row.ship_to_name ||
                        PO_EMPTY_TEXT}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      รหัสในไฟล์ {row.ship_to_code}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.po_number}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatCalendarDate(row.delivery_date)}
                  </TableCell>
                  <TableCell align="right">{formatNumber(row.amount)}</TableCell>
                  <TableCell align="center">
                    <PurchaseOrderStatusChip status={row.status} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <PurchaseOrderPagination
        totalCount={list.totalCount}
        page={list.page}
        limit={list.limit}
        onChangePage={list.changePage}
        onChangeLimit={list.changeLimit}
      />
    </Stack>
  );
}

/**
 * รายการ PO ของหนึ่งช่องในตาราง "ตามสาย × วัน" ของ W7 (หรือรวมทั้งแถว/ทั้งคอลัมน์)
 * · คลิกแถวเพื่อเปิดรายละเอียด W6 ตัวเดิม
 */
export default function WeeklySliceDrawer({
  slice,
  onClose,
  onOpenDetail,
}: WeeklySliceDrawerProps) {
  return (
    <Drawer
      anchor="right"
      open={Boolean(slice)}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: { width: { xs: "100%", md: DRAWER_WIDTH }, maxWidth: "100%", p: 0 },
        },
      }}
    >
      <Stack sx={{ height: "100%" }}>
        <Box sx={{ p: 3, pb: 0, [PO_COMPACT_MEDIA]: { p: 2, pb: 0 } }}>
          <Typography variant="body1" fontWeight={600} gutterBottom>
            {slice?.title ?? ""}
          </Typography>
          <Divider />
        </Box>

        <Box
          sx={{
            p: 3,
            [PO_COMPACT_MEDIA]: { p: 2 },
            overflowY: "auto",
            flex: 1,
          }}
        >
          {slice && <SliceList slice={slice} onOpenDetail={onOpenDetail} />}
        </Box>

        <Stack
          direction="row"
          justifyContent="flex-end"
          sx={{
            p: 3,
            pt: 2,
            [PO_COMPACT_MEDIA]: { p: 2, pt: 1.5 },
            borderTop: (theme) => `1px solid ${theme.palette.draft.main}`,
          }}
        >
          <Button onClick={onClose}>ปิด</Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
